import { useEffect, useMemo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import { usePanelToast } from '../../../context/PanelToastContext';
import { usePlatformSettings } from '../../../context/PlatformSettingsContext';
import SaasPlanForm from './SaasPlanForm';
import {
  fetchSaasPlans,
  createSaasPlan,
  updateSaasPlan,
  deleteSaasPlan,
} from '../../../services/owner/subscriptionsApi';
import { peekOwnerCache } from '../../../services/cache/ownerCache';
import '../ownerPages.css';
import './subscriptions.css';

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function getPlanDurationLabel(plan) {
  if (Number(plan.monthlyPrice) > 0) return 'Monthly';
  if (Number(plan.quarterlyPrice) > 0) return 'Quarterly';
  if (Number(plan.yearlyPrice) > 0) return 'Yearly';
  if (Number(plan.lifetimePrice) > 0) return 'Lifetime';
  return '—';
}

function getPlanDisplayPrice(plan) {
  if (Number(plan.monthlyPrice) > 0) return plan.monthlyPrice;
  if (Number(plan.quarterlyPrice) > 0) return plan.quarterlyPrice;
  if (Number(plan.yearlyPrice) > 0) return plan.yearlyPrice;
  if (Number(plan.lifetimePrice) > 0) return plan.lifetimePrice;
  return 0;
}

function formatFeaturesPreview(features) {
  const lines = String(features || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return '—';
  if (lines.length <= 2) return lines.join(' · ');
  return `${lines.slice(0, 2).join(' · ')} +${lines.length - 2} more`;
}

function SubscriptionPackages() {
  const toast = usePanelToast();
  const { formatPrice } = usePlatformSettings();
  const cachedPlans = peekOwnerCache('saas-plans');
  const [plans, setPlans] = useState(cachedPlans || []);
  const [loading, setLoading] = useState(!cachedPlans);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const loadPlans = async () => {
    const data = await fetchSaasPlans();
    setPlans(data);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!peekOwnerCache('saas-plans')) setLoading(true);
        setError('');
        await loadPlans();
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load packages');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredPlans = useMemo(() => {
    const query = search.trim().toLowerCase();
    return plans.filter((plan) => {
      const matchesSearch =
        !query ||
        plan.name.toLowerCase().includes(query) ||
        plan.id.toLowerCase().includes(query) ||
        plan.features.toLowerCase().includes(query);
      const matchesStatus = statusFilter === 'all' || plan.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [plans, search, statusFilter]);

  const openCreateModal = () => {
    setFormKey((k) => k + 1);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => setShowCreateModal(false);

  const openEditModal = (plan) => {
    setFormKey((k) => k + 1);
    setEditTarget(plan);
  };

  const closeEditModal = () => setEditTarget(null);

  const handleCreateSubmit = async (form) => {
    try {
      setSaving(true);
      const created = await createSaasPlan(form);
      setPlans((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
      closeCreateModal();
      toast.showAdded('Package');
    } catch (err) {
      toast.showError(err.message || 'Failed to create package');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (form) => {
    try {
      setSaving(true);
      const updated = await updateSaasPlan(editTarget.id, form);
      setPlans((prev) => prev.map((p) => (p.id === editTarget.id ? updated : p)));
      closeEditModal();
      toast.showUpdated('Package');
    } catch (err) {
      toast.showError(err.message || 'Failed to update package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await deleteSaasPlan(deleteTarget.id);
      setPlans((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.showDeleted('Package');
    } catch (err) {
      toast.showError(err.message || 'Failed to delete package');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Subscription Packages"
        subtitle="Create plans that appear on the gym signup page"
      >
        <button type="button" className="owner-btn owner-btn-primary" onClick={openCreateModal}>
          Create Package
        </button>
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading packages...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="rec-member-toolbar admin-list-toolbar">
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search packages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="owner-form-input rec-member-toolbar-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table owner-panel-table">
            <thead>
              <tr>
                <th>Package</th>
                <th>Duration</th>
                <th>Price</th>
                <th>Features</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.length > 0 ? (
                filteredPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.name}</td>
                    <td>{getPlanDurationLabel(plan)}</td>
                    <td>{formatPrice(getPlanDisplayPrice(plan))}</td>
                    <td>{formatFeaturesPreview(plan.features)}</td>
                    <td><StatusBadge status={plan.status} /></td>
                    <td>
                      <div className="rec-member-actions-cell">
                        <button
                          type="button"
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          onClick={() => openEditModal(plan)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="owner-btn owner-btn-sm rec-member-btn-delete"
                          onClick={() => setDeleteTarget(plan)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No packages yet. Create one to show on signup.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <div className="subscription-modal-overlay" onClick={closeCreateModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Create Subscription Package</h2>
              <button type="button" className="subscription-modal-close" onClick={closeCreateModal} aria-label="Close">×</button>
            </div>
            <SaasPlanForm
              key={formKey}
              submitLabel={saving ? 'Creating…' : 'Create Package'}
              onSubmit={handleCreateSubmit}
              onCancel={closeCreateModal}
            />
          </div>
        </div>
      )}

      {editTarget && (
        <div className="subscription-modal-overlay" onClick={closeEditModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Edit Package — {editTarget.name}</h2>
              <button type="button" className="subscription-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
            </div>
            <SaasPlanForm
              key={formKey}
              isEdit
              initialData={editTarget}
              submitLabel={saving ? 'Saving…' : 'Save Changes'}
              onSubmit={handleEditSubmit}
              onCancel={closeEditModal}
            />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Package</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="rec-member-modal-message">
              Delete <strong>{deleteTarget.name}</strong>? It will be removed from the signup page. This cannot be undone if no gyms use it.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionPackages;
